/**
 * Authentication Manager (ES Module)
 * Zentrales Auth-System - verhindert Loops durch Event-basierte Architektur
 */

export class AuthManager {
    constructor(supabase) {
        this.supabase = supabase;
        this.currentUser = null;
        this.isAuthenticated = false;
        this.listeners = [];
        this.initialized = false;
        this.authStateChangeListener = null;
    }

    async init() {
        if (this.initialized) return;
        try {
            const { data: { session }, error } = await this.supabase.auth.getSession();
            if (error) {
                console.error('❌ Fehler beim Laden der Session:', error);
                this.isAuthenticated = false;
                this.currentUser = null;
            } else if (session && session.user) {
                this.currentUser = session.user;
                this.isAuthenticated = true;
                this.updateUI();
            } else {
                this.isAuthenticated = false;
                this.currentUser = null;
            }
            this.setupAuthStateListener();
            this.initialized = true;
            this.notifyListeners();
        } catch (error) {
            console.error('❌ Fehler bei AuthManager-Initialisierung:', error);
            this.isAuthenticated = false;
            this.currentUser = null;
        }
    }

    setupAuthStateListener() {
        if (this.authStateChangeListener) {
            this.authStateChangeListener.subscription.unsubscribe();
        }
        const { data: { subscription } } = this.supabase.auth.onAuthStateChange(
            async (event, session) => {
                switch (event) {
                    case 'SIGNED_IN':
                        this.currentUser = session.user;
                        this.isAuthenticated = true;
                        this.updateUI();
                        this.notifyListeners();
                        break;
                    case 'SIGNED_OUT':
                        this.currentUser = null;
                        this.isAuthenticated = false;
                        this.updateUI();
                        this.notifyListeners();
                        break;
                    case 'TOKEN_REFRESHED':
                        if (session && session.user) {
                            this.currentUser = session.user;
                            this.isAuthenticated = true;
                        }
                        break;
                    case 'USER_UPDATED':
                        if (session && session.user) {
                            this.currentUser = session.user;
                            this.updateUI();
                        }
                        break;
                }
            }
        );
        this.authStateChangeListener = { subscription };
    }

    updateUI() {
        if (!this.isAuthenticated || !this.currentUser) return;
        const userNameEl = document.getElementById('user-name');
        if (userNameEl) {
            const name = this.currentUser.user_metadata?.name ||
                this.currentUser.email?.split('@')[0] || 'Benutzer';
            userNameEl.textContent = name;
        }
        if (typeof window !== 'undefined' && window.app) {
            setTimeout(() => {
                if (typeof window.app.loadProfileImage === 'function') window.app.loadProfileImage();
                if (typeof window.app.loadUserName === 'function') window.app.loadUserName();
            }, 100);
        }
    }

    async login(email, password) {
        const { data, error } = await this.supabase.auth.signInWithPassword({
            email: email.trim(),
            password: password
        });
        if (error) {
            console.error('❌ Login-Fehler:', error.message);
            throw new Error(this.getAuthErrorMessage(error.message));
        }
        if (!data.user || !data.session) {
            throw new Error('Login fehlgeschlagen - keine Benutzerdaten erhalten');
        }
        return true;
    }

    async signUp(email, password, metadata = {}, options = {}) {
        const trimmedEmail = String(email || '').trim().toLowerCase();
        if (!trimmedEmail) {
            throw new Error('E-Mail ist erforderlich');
        }

        const cleanedPassword = String(password || '');
        if (!cleanedPassword) {
            throw new Error('Passwort ist erforderlich');
        }

        const signUpOptions = {
            data: metadata || {}
        };
        if (options && typeof options === 'object') {
            const emailRedirectTo = String(options.emailRedirectTo || '').trim();
            if (emailRedirectTo) {
                signUpOptions.emailRedirectTo = emailRedirectTo;
            }
        }

        const { data, error } = await this.supabase.auth.signUp({
            email: trimmedEmail,
            password: cleanedPassword,
            options: signUpOptions
        });

        if (error) {
            console.error('❌ Signup-Fehler:', error.message);
            throw new Error(this.getAuthErrorMessage(error.message));
        }

        return data || null;
    }

    async logout() {
        try {
            const { data: { session }, error: sessionError } = await this.supabase.auth.getSession();
            if (session && !sessionError) {
                const { error } = await this.supabase.auth.signOut({ scope: 'local' });
                if (error && !error.message?.includes('Auth session missing')) {
                    console.error('❌ Logout-Fehler:', error);
                }
            }
            localStorage.removeItem('user');
            localStorage.removeItem('supabase_session');
            const supabaseStorageKey = 'visualize-supabase-auth';
            localStorage.removeItem(supabaseStorageKey);
            try {
                const urlParts = this.supabase.supabaseUrl.split('//')[1]?.split('.');
                if (urlParts && urlParts.length > 0) {
                    const projectRef = urlParts[0];
                    localStorage.removeItem(`sb-${projectRef}-auth-token`);
                }
            } catch (e) {}
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && (key.includes('supabase') || key.startsWith('sb-') || key.startsWith('visualize-supabase') || key.includes('auth-token'))) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
            sessionStorage.clear();
            this.currentUser = null;
            this.isAuthenticated = false;
            return true;
        } catch (error) {
            console.error('❌ Logout-Fehler:', error);
            localStorage.removeItem('user');
            localStorage.removeItem('supabase_session');
            localStorage.removeItem('visualize-supabase-auth');
            try {
                const urlParts = this.supabase.supabaseUrl.split('//')[1]?.split('.');
                if (urlParts && urlParts.length > 0) {
                    localStorage.removeItem(`sb-${urlParts[0]}-auth-token`);
                }
            } catch (e) {}
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && (key.includes('supabase') || key.startsWith('sb-') || key.includes('auth-token'))) keysToRemove.push(key);
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
            sessionStorage.clear();
            this.currentUser = null;
            this.isAuthenticated = false;
            if (error.message?.includes('Auth session missing')) return true;
            throw error;
        }
    }

    addListener(callback) {
        this.listeners.push(callback);
        callback(this.isAuthenticated, this.currentUser);
    }

    removeListener(callback) {
        this.listeners = this.listeners.filter(l => l !== callback);
    }

    notifyListeners() {
        this.listeners.forEach(cb => {
            try { cb(this.isAuthenticated, this.currentUser); } catch (e) { console.error(e); }
        });
    }

    getAuthErrorMessage(errorMessage) {
        const isEnglish = !!(window.i18n && window.i18n.getCurrentLanguage && window.i18n.getCurrentLanguage() === 'en');
        const errorMap = isEnglish
            ? {
                'Invalid login credentials': 'Invalid credentials. Please check email and password.',
                'Email not confirmed': 'Email address has not been confirmed yet.',
                'Too many requests': 'Too many login attempts. Please wait a moment.',
                'User not found': 'User not found.',
                'Invalid email': 'Invalid email address.',
                'Password should be at least 6 characters': 'Password must be at least 6 characters long.',
                'User already registered': 'An account for this email already exists. Please sign in.',
                'Signup is disabled': 'Sign-up is currently disabled. Please contact an admin.'
            }
            : {
                'Invalid login credentials': 'Ungültige Anmeldedaten. Bitte überprüfe E-Mail und Passwort.',
                'Email not confirmed': 'E-Mail-Adresse wurde noch nicht bestätigt.',
                'Too many requests': 'Zu viele Anmeldeversuche. Bitte warte einen Moment.',
                'User not found': 'Benutzer nicht gefunden.',
                'Invalid email': 'Ungültige E-Mail-Adresse.',
                'Password should be at least 6 characters': 'Passwort muss mindestens 6 Zeichen lang sein.',
                'User already registered': 'Für diese E-Mail existiert bereits ein Konto. Bitte einloggen.',
                'Signup is disabled': 'Registrierung ist derzeit deaktiviert. Bitte Admin kontaktieren.'
            };
        return errorMap[errorMessage] || `${isEnglish ? 'Error' : 'Fehler'}: ${errorMessage}`;
    }

    destroy() {
        if (this.authStateChangeListener) {
            this.authStateChangeListener.subscription.unsubscribe();
        }
        this.listeners = [];
        this.initialized = false;
    }
}
